import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate, useParams } from "react-router-dom";
import { FiSave, FiX, FiArrowLeft } from "react-icons/fi";
import "./AddFormModule.css";

function AddFormModule() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [originalRecord, setOriginalRecord] = useState(null);

  const [formData, setFormData] = useState({
    module_code: "",
    module_name: "",
    description: "",
    status: "DRAFT",
  });

  useEffect(() => {
    if (isEditMode) {
      fetchModule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchModule = async () => {
    setFetching(true);

    try {
      const res = await axios.get(`${API_BASE}/form-modules/${id}/`);
      const item = res.data?.data || res.data;

      setOriginalRecord(item);

      setFormData({
        module_code: item.module_code || "",
        module_name: item.module_name || "",
        description: item.description || "",
        status: item.status || "DRAFT",
      });
    } catch (error) {
      console.error("Error fetching form module:", error);
      Swal.fire("Error", "Failed to load form module details", "error");
      navigate("/catalog/form-modules");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.module_code.trim() || !formData.module_name.trim()) {
      Swal.fire(
        "Validation Error",
        "Module Code and Module Name are required",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      if (isEditMode) {
        const payload = {
          module_code: formData.module_code.trim(),
          module_name: formData.module_name.trim(),
          version: originalRecord?.version ?? 1,
          description: formData.description.trim(),
          status: formData.status,
          effective_from:
            originalRecord?.effective_from ||
            new Date().toISOString().split("T")[0],
          published_at: originalRecord?.published_at || null,
          retired_at: originalRecord?.retired_at || null,
          created_by: originalRecord?.created_by ?? 1,
        };

        await axios.put(`${API_BASE}/form-modules/${id}/`, payload);

        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Form module has been updated.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        const payload = {
          module_code: formData.module_code.trim(),
          module_name: formData.module_name.trim(),
          version: 1,
          description: formData.description.trim(),
          status: formData.status,
          effective_from: new Date().toISOString().split("T")[0],
          published_at: null,
          retired_at: null,
          created_by: 1,
        };

        await axios.post(`${API_BASE}/form-modules/`, payload);

        Swal.fire({
          icon: "success",
          title: "Added!",
          text: "Form module has been created.",
          timer: 2000,
          showConfirmButton: false,
        });
      }

      navigate("/catalog/form-modules");
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} form module:`,
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} form module`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (fetching) {
    return (
      <div className="app-layout">
        <Sidebar />

        <div className="main-content">
          <Header />

          <div className="form-module-form-page">
            <div className="form-module-form-loader">
              <div className="form-module-form-spinner"></div>
              <p>Loading form module...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="form-module-form-page">
          <div className="form-module-form-top">
            <button
              type="button"
              className="form-module-form-back-btn"
              onClick={handleBack}
              title="Back"
            >
              <FiArrowLeft />
            </button>

            <div className="form-module-form-page-heading">
              <h2>
                {isEditMode ? "Edit Form Module" : "Add Form Module"}
              </h2>
            </div>
          </div>

          <div className="form-module-form-card">
            <form onSubmit={handleSubmit}>
              <div className="form-module-form-grid">
                <div className="form-module-form-field">
                  <label htmlFor="module_code">
                    Module Code{" "}
                    <span className="form-module-form-required">*</span>
                  </label>

                  <input
                    type="text"
                    id="module_code"
                    name="module_code"
                    placeholder="MOD-001"
                    value={formData.module_code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-module-form-field">
                  <label htmlFor="module_name">
                    Module Name{" "}
                    <span className="form-module-form-required">*</span>
                  </label>

                  <input
                    type="text"
                    id="module_name"
                    name="module_name"
                    placeholder="Site Inspection Checklist"
                    value={formData.module_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-module-form-field">
                  <label htmlFor="status">
                    Status{" "}
                    <span className="form-module-form-required">*</span>
                  </label>

                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="PUBLISHED">Published</option>
                    <option value="RETIRED">Retired</option>
                  </select>
                </div>

                <div className="form-module-form-field form-module-form-field-full">
                  <label htmlFor="description">Description</label>

                  <textarea
                    id="description"
                    name="description"
                    placeholder="Brief description of this module..."
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>
              </div>

              <div className="form-module-form-actions">
                <button
                  type="button"
                  className="form-module-form-cancel-btn"
                  onClick={handleBack}
                  disabled={loading}
                >
                  <FiX />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="form-module-form-save-btn"
                  disabled={loading}
                >
                  <FiSave />
                  {loading
                    ? "Saving..."
                    : isEditMode
                    ? "Update Module"
                    : "Save Module"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddFormModule;